import React, { createContext, useContext, ReactNode, useCallback } from 'react';

interface HeaderButton {
  id: string;
  element: ReactNode;
}

interface HeaderContextType {
  breadcrumb?: ReactNode;
  headerButtons: HeaderButton[];
  setBreadcrumb: (breadcrumb: ReactNode) => void;
  addHeaderButton: (id: string, button: ReactNode) => void;
  removeHeaderButton: (id: string) => void;
  clearHeaderButtons: () => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const HeaderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [breadcrumb, setBreadcrumb] = React.useState<ReactNode>();
  const [headerButtons, setHeaderButtons] = React.useState<HeaderButton[]>([]);

  const addHeaderButton = useCallback((id: string, button: ReactNode) => {
    setHeaderButtons((prev) => {
      // Remove any existing button with the same ID
      const filtered = prev.filter((btn) => btn.id !== id);
      return [...filtered, { id, element: button }];
    });
  }, []);

  const removeHeaderButton = useCallback((id: string) => {
    setHeaderButtons((prev) => prev.filter((btn) => btn.id !== id));
  }, []);

  const clearHeaderButtons = useCallback(() => {
    setHeaderButtons([]);
  }, []);

  const setBreadcrumbMemoized = useCallback((breadcrumb: ReactNode) => {
    setBreadcrumb(breadcrumb);
  }, []);

  return (
    <HeaderContext.Provider
      value={{
        breadcrumb,
        headerButtons,
        setBreadcrumb: setBreadcrumbMemoized,
        addHeaderButton,
        removeHeaderButton,
        clearHeaderButtons,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
};

export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};
